<script>
    import { onMount } from "svelte";

    import { defaultFilters, user } from "../../../stores";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        areObjectEqual,
        getPasswordValidation,
        isObjectEmpty,
        replaceURL,
        showError,
        showException,
    } from "../../../Utils/utilFunctions.svelte";

    import {
        authenticatorTypes,
        DirectUserRoles,
    } from "../../../Utils/utilEnums.svelte";

    import {
        Loader,
        PasswordInput,
    } from "../../../Components/Common/index.svelte";

    import { cacheDefaultFilters } from "../../../Utils/utilCache.svelte";

    export let userDetails = {},
        goToOverviewFunc = () => {};

    let displayUser = {},
        newPwd = "",
        confirmNewPwd = "";

    let isGettingMfaCode = false,
        qrCodeContent,
        qrCode;

    let userSettings = {},
        defaultUserSettings = {};

    let userDefaultFilters = [],
        defaultUserDefaultFilters = [];

    let isLoadingDefaultDoctorFilters = false,
        defaultDoctorFilterOptions = [],
        selectedDoctorFilter = {};

    $: originalUser = { ...userDetails };

    $: isUserDefaultAuthenticatedByGoogle = originalUser.mfa == 4;
    $: isUserNewAuthenticatedByGoogle = displayUser.mfa == 4;

    $: isPwdSame = newPwd.toString() == confirmNewPwd.toString();

    $: isValidPwd = newPwd ? getPasswordValidation(newPwd) : true;

    $: isValidQrCode =
        displayUser.mfa == 4 ? (qrCode ? qrCode.length == 6 : false) : true;

    $: isUserEnableChangeDefaultDoctorFilters =
        $user.role == DirectUserRoles.Admin ||
        $user.role == DirectUserRoles.Manager ||
        $user.role == DirectUserRoles.Support;

    $: isProfileDetailsNotChanged =
        originalUser.fullName == displayUser.fullName &&
        originalUser.phone == displayUser.phone &&
        originalUser.mfa == displayUser.mfa &&
        areObjectEqual(userSettings, defaultUserSettings) &&
        areObjectEqual(userDefaultFilters, defaultUserDefaultFilters) &&
        !newPwd;

    $: shouldApplyMfaCode =
        (isUserDefaultAuthenticatedByGoogle ||
            isUserNewAuthenticatedByGoogle) &&
        (newPwd || confirmNewPwd)
            ? true
            : false;

    $: saveButtonDisable =
        isProfileDetailsNotChanged ||
        (shouldApplyMfaCode ? !isValidQrCode : false) ||
        !isValidPwd ||
        !isPwdSame;

    onMount(async () => {
        if (originalUser) displayUser = { ...originalUser };

        if ($user.settings && !isObjectEmpty($user.settings)) {
            userSettings = { ...$user.settings };
            defaultUserSettings = { ...$user.settings };
        }

        if (isUserEnableChangeDefaultDoctorFilters)
            await loadDefaultDoctorFilterOptions();

        // read and preset selected doctor filter option
        if (displayUser.defaultFilters) {
            userDefaultFilters = displayUser.defaultFilters;
            defaultUserDefaultFilters = userDefaultFilters;
            selectedDoctorFilter =
                userDefaultFilters.find(
                    (filter) =>
                        filter.filterType == 0 &&
                        $defaultFilters?.userFilter.some(
                            (f) => f.id == filter.filterId,
                        ),
                ) ?? {};
        }
    });

    const loadDefaultDoctorFilterOptions = async () => {
        isLoadingDefaultDoctorFilters = true;

        if (!$defaultFilters || !$defaultFilters.userFilter)
            await cacheDefaultFilters();

        defaultDoctorFilterOptions = [...$defaultFilters.userFilter];

        isLoadingDefaultDoctorFilters = false;
    };

    const saveUserDetail = async () => {
        if (newPwd) displayUser.password = newPwd;

        if (selectedDoctorFilter)
            displayUser.defaultFilters = [...userDefaultFilters];

        if (shouldApplyMfaCode) await validateMfa();
        else updateUser();
    };

    const validateMfa = async () => {
        await apiMethods.postMethod
            .validateMfa({ userId: displayUser.id, code: qrCode })
            .then((res) => {
                if (res) updateUser();
                else showError("Incorrect 2FA code");
            })
            .catch((err) => showException(err));
    };

    const updateUser = async () => {
        $user.settings = { ...userSettings };
        displayUser.settings = JSON.stringify(userSettings);

        await apiMethods.putMethod
            .updateUser(displayUser)
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        "Failed to update user details, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    $user.name = res.fullName;

                    if (selectedDoctorFilter) {
                        if ($user.defaultFilters?.length)
                            $user.defaultFilters = $user.defaultFilters.map(
                                (filter) =>
                                    filter.filterType == 0
                                        ? selectedDoctorFilter
                                        : filter,
                            );
                        else
                            $user.defaultFilters = [
                                { ...selectedDoctorFilter },
                            ];
                    }

                    // when change password auto-login to refresh token
                    if (newPwd) {
                        const signInData = {
                            email: res.email,
                            password: newPwd,
                        };

                        await apiMethods.postMethod
                            .logIn(signInData)
                            .then((res) => {
                                $user.token = res.token;
                            });
                    }

                    goToOverviewFunc();
                }
            })
            .catch((err) => showException(err));
    };

    // HELPER FUNCTIONS
    const _handleSelectDefaultDoctorFilter = (e) => {
        const selectedDoctorFilterId = e.target.value;

        selectedDoctorFilter = {
            filterType: Number(0),
            filterId: Number(selectedDoctorFilterId),
        };

        if (!userDefaultFilters?.length)
            userDefaultFilters = [selectedDoctorFilter];
        else
            userDefaultFilters = userDefaultFilters.map((filter) =>
                filter.filterType === 0 ? selectedDoctorFilter : filter,
            );
    };

    const _handleAuthenticatorTypeChange = async (e) => {
        // Instead of binding value to update, update mfa via function to avoid Firefox trigger function before update value
        displayUser.mfa = parseInt(e.target.value);

        if (displayUser.mfa == 4) {
            isGettingMfaCode = true;

            await apiMethods.postMethod
                .getMfaQrCode(displayUser.id)
                .then((res) => {
                    if (res) qrCodeContent = res;
                    else
                        showError(
                            "Failed to load QR Code, please refresh the page and try again and contact support if the issue persists.",
                        );
                })
                .catch((err) =>
                    showException(
                        "Failed to load QR Code, please refresh the page and try again and contact support if the issue persists.",
                    ),
                );

            const qrCodeInput = document.getElementById("qrCodeInput");

            qrCodeInput.focus();

            isGettingMfaCode = false;
        } else {
            qrCodeContent = "";
            qrCode = "";
        }
    };

    $: isUserDoctorOrPatient = $user.role <= 2;
</script>

<div class="flex flex-col px-4 gap-y-2">
    <form
        class="flex flex-col flex-1 w-full gap-y-4"
        on:submit|preventDefault={saveUserDetail}
    >
        <div
            class="flex flex-1 items-center justify-between py-2 border-b border-gray-400 sticky top-0 bg-white"
            style="z-index: 1;"
        >
            <p class="font-[700] text-[18px] text-black w-full">Profile</p>
        </div>

        <div class="flex flex-col flex-1 w-full gap-y-4 z-1">
            <div class="flex flex-1 items-center">
                <p class="profileTitle">Full name</p>
                <input
                    type="text"
                    class="input input-bordered input-sm w-full max-w-xs"
                    bind:value={displayUser.fullName}
                />
            </div>

            <div class="flex flex-1 items-center">
                <p class="profileTitle">Phone</p>
                <input
                    type="phone"
                    class="input input-bordered input-sm w-full max-w-xs"
                    bind:value={displayUser.phone}
                />
            </div>

            <div class="flex flex-1 items-start">
                <p class="profileTitle">New Password</p>
                <div class="w-full max-w-xs z-0">
                    <PasswordInput bind:value={newPwd} isRequired />
                </div>
            </div>

            <div class="flex flex-1 items-center">
                <p class="profileTitle">Confirm Password</p>
                <div class="w-full max-w-xs z-0">
                    <PasswordInput
                        bind:value={confirmNewPwd}
                        pwdValue={newPwd}
                        isRequired
                        isConfirm
                    />
                </div>
            </div>

            <div class="flex flex-1 items-center">
                <p class="profileTitle">2FA Authentication</p>
                <!-- Not binding value as Firefox triggers function before value updates -->
                <select
                    class="select select-sm select-bordered w-full max-w-xs"
                    on:input={_handleAuthenticatorTypeChange}
                >
                    {#if displayUser.mfa == 0}
                        <option value={0} disabled selected>NONE</option>
                    {/if}
                    {#each authenticatorTypes.filter( (type) => (type.visibleUserRole ? type.visibleUserRole.includes($user.role) : true), ) as type}
                        <option
                            value={type.id}
                            selected={type.id == originalUser.mfa}
                        >
                            {type.type}
                        </option>
                    {/each}
                </select>
            </div>

            {#if isUserNewAuthenticatedByGoogle}
                <div class="flex flex-1 items-center">
                    <p class="profileTitle" />

                    <div class="flex flex-col gap-y-2 w-full max-w-xs">
                        {#if qrCodeContent}
                            <img src={qrCodeContent} alt="QR Code" />
                        {/if}
                        <input
                            type="text"
                            class="input input-sm input-bordered w-full {shouldApplyMfaCode
                                ? 'outline outline-2 outline-[#ff0000]'
                                : ''}"
                            id="qrCodeInput"
                            placeholder="6-digits 2FA Code"
                            autocomplete="off"
                            bind:value={qrCode}
                        />
                    </div>
                </div>
            {/if}
        </div>

        <div
            class="flex flex-1 items-center justify-between py-2 border-b border-gray-400 sticky top-0 bg-white"
            style="z-index: 1;"
        >
            <p class="font-[700] text-[18px] text-black">Settings</p>
        </div>

        {#if isUserEnableChangeDefaultDoctorFilters}
            <div class="flex flex-1 items-center">
                <p class="profileTitle">Default Filter - Doctors</p>

                {#if isLoadingDefaultDoctorFilters}
                    <Loader />
                {:else}
                    <select
                        class="input input-sm input-bordered w-full bg-white max-w-xs"
                        on:change={_handleSelectDefaultDoctorFilter}
                    >
                        <option
                            value=""
                            selected={!selectedDoctorFilter ||
                                isObjectEmpty(selectedDoctorFilter)}
                            on:select={() => (selectedDoctorFilter = {})}
                        >
                            Select default doctors filter
                        </option>
                        {#each defaultDoctorFilterOptions as { id, name }}
                            <option
                                value={id}
                                selected={selectedDoctorFilter &&
                                    Number(selectedDoctorFilter.filterId) == id}
                            >
                                {name}
                            </option>
                        {/each}
                    </select>
                {/if}
            </div>
        {/if}

        <div class="flex flex-1 items-center">
            <label for="tableActionUseHamburgerIcon" class="profileTitle">
                Show Collapsed Table Actions
            </label>
            <input
                type="checkbox"
                id="tableActionUseHamburgerIcon"
                class="toggle toggle-secondary"
                bind:checked={userSettings.tableActionUseHamburgerIcon}
            />
        </div>

        <div class="flex flex-1 items-center">
            <label for="showEncountersOnDailyPlanner" class="profileTitle">
                Show Encounters on Daily Planner
            </label>
            <input
                type="checkbox"
                id="showEncountersOnDailyPlanner"
                class="toggle toggle-secondary"
                bind:checked={userSettings.showEncountersOnDailyPlanner}
            />
        </div>

        <div class="flex flex-1 items-center">
            <label for="alwaysShowAllDayEvents" class="profileTitle">
                Always Show All-Day Events
            </label>
            <input
                type="checkbox"
                id="alwaysShowAllDayEvents"
                class="toggle toggle-secondary"
                bind:checked={userSettings.alwaysShowAllDayEvents}
            />
        </div>

        {#if !isUserDoctorOrPatient}
            <div class="flex flex-1 items-center">
                <label for="alwaysShowAllDayEvents" class="profileTitle">
                    Daily Planner Vertical View
                </label>
                <input
                    type="checkbox"
                    id="alwaysShowAllDayEvents"
                    class="toggle toggle-secondary"
                    bind:checked={userSettings.dailyPlannerVerticalView}
                />
            </div>
        {/if}

        <div class="flex flex-1 items-center">
            <label for="disableDateRangeInput" class="profileTitle">
                Disable Date Range Input
            </label>
            <input
                type="checkbox"
                id="disableDateRangeInput"
                class="toggle toggle-secondary"
                bind:checked={userSettings.disableDateRangeInput}
            />
        </div>

        <div class="flex flex-1 items-center mt-32">
            <p class="profileTitle" />
            <button
                class="btn btn-secondary btn-sm w-full max-w-xs"
                on:click|preventDefault={saveUserDetail}
                disabled={saveButtonDisable}
            >
                Save
            </button>
        </div>
    </form>
</div>

<style>
    .profileTitle {
        line-height: 2rem;
        cursor: default;
    }

    .profileTitle {
        width: 33%;
        color: gray;
    }
</style>
